import { Controller } from "@hotwired/stimulus"

// Live SEO panel for the blog post editor: character counters on the
// meta_title/meta_description fields, a Google-snippet preview, and an SEO
// score ring. One delegated `input`/`change` listener on the controller's
// root element (the whole `<form>`) covers every source field without
// per-field data-action wiring — same pattern as card_dirty_controller.js:
// plain inputs/textareas fire native "input", and the shared composer
// island's hidden field dispatches a bubbling synthetic "input" event on
// every edit (EmailComposerIsland), so body edits are picked up too.
const TITLE_LIMIT = 60
const TITLE_MIN = 20
const DESCRIPTION_LIMIT = 160
const DESCRIPTION_MIN = 70
const MIN_BODY_WORDS = 300

export default class extends Controller {
  static targets = [
    "titleInput", "metaTitleInput", "titleCounter",
    "excerptInput", "metaDescriptionInput", "descriptionCounter",
    "slugInput",
    "bodyInput",
    "previewTitle", "previewUrl", "previewDescription",
    "scoreRing", "scoreValue", "scoreHint",
    "wordCount",
  ]

  static values = {
    // "goatleads.com/blog/" — prefixed onto the slug field's live value to
    // build the snippet preview's URL line.
    slugBase: { type: String, default: "" },
    // Whether a cover image is currently attached. Read from a static data
    // value (set server-side from `@blog_post.cover_image.attached?`) rather
    // than a target because the cover-image upload slot lives in its own
    // partial and there is no single DOM node whose mere presence means
    // "attached" the way a target's presence would.
    hasCover: { type: Boolean, default: false },
  }

  connect() {
    this._boundUpdate = this.update.bind(this)
    this.element.addEventListener("input", this._boundUpdate)
    this.element.addEventListener("change", this._boundUpdate)
    this.update()
  }

  disconnect() {
    this.element.removeEventListener("input", this._boundUpdate)
    this.element.removeEventListener("change", this._boundUpdate)
  }

  update() {
    this._updateCounters()
    this._updatePreview()
    this._updateWordCount()
    this._updateScore()
  }

  // ---- Fallback-chain effective values ----
  // The raw meta fields are what the counters measure; the EFFECTIVE
  // title/description (meta field if present, else the fallback) is what a
  // search engine would actually render, so both the snippet preview and the
  // SEO score's length checks read the effective value, not the raw field.

  _effectiveTitle() {
    const metaTitle = this.hasMetaTitleInputTarget ? this.metaTitleInputTarget.value.trim() : ""
    if (metaTitle) return metaTitle
    return this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : ""
  }

  _effectiveDescription() {
    const metaDescription = this.hasMetaDescriptionInputTarget ? this.metaDescriptionInputTarget.value.trim() : ""
    if (metaDescription) return metaDescription
    return this.hasExcerptInputTarget ? this.excerptInputTarget.value.trim() : ""
  }

  _bodyValue() {
    return this.hasBodyInputTarget ? (this.bodyInputTarget.value || "") : ""
  }

  _wordCount(html) {
    const text = html.replace(/<[^>]+>/g, " ")
    return text.split(/\s+/).filter(Boolean).length
  }

  // ---- Counters (raw meta field length, independent of the fallback chain) ----

  _updateCounters() {
    if (this.hasMetaTitleInputTarget && this.hasTitleCounterTarget) {
      const len = this.metaTitleInputTarget.value.length
      this.titleCounterTarget.textContent = `${len}/${TITLE_LIMIT}`
      this._paintCounter(this.titleCounterTarget, len <= TITLE_LIMIT)
    }
    if (this.hasMetaDescriptionInputTarget && this.hasDescriptionCounterTarget) {
      const len = this.metaDescriptionInputTarget.value.length
      this.descriptionCounterTarget.textContent = `${len}/${DESCRIPTION_LIMIT}`
      this._paintCounter(this.descriptionCounterTarget, len <= DESCRIPTION_LIMIT)
    }
  }

  _paintCounter(el, ok) {
    el.classList.toggle("gl-blog-seo-counter--ok", ok)
    el.classList.toggle("gl-blog-seo-counter--warn", !ok)
  }

  // ---- Google-snippet preview ----

  _updatePreview() {
    if (this.hasPreviewTitleTarget) {
      this.previewTitleTarget.textContent = this._effectiveTitle() || "Untitled post"
    }
    if (this.hasPreviewDescriptionTarget) {
      this.previewDescriptionTarget.textContent = this._effectiveDescription()
    }
    if (this.hasPreviewUrlTarget) {
      const slug = this.hasSlugInputTarget ? this.slugInputTarget.value.trim() : ""
      this.previewUrlTarget.textContent = `${this.slugBaseValue}${slug}`
    }
  }

  // ---- Word-count footer ----

  _updateWordCount() {
    if (this.hasWordCountTarget) this.wordCountTarget.textContent = `${this._wordCount(this._bodyValue())}`
  }

  // ---- SEO score ----
  // Six checks, evaluated in this fixed order. `scoreHint` surfaces the FIRST
  // failing check's message — fixing checks top-to-bottom always clears the
  // most prominent complaint first, rather than jumping around as later
  // checks resolve out of order.
  _checks() {
    const title = this._effectiveTitle()
    const description = this._effectiveDescription()
    const body = this._bodyValue()
    const wordCount = this._wordCount(body)

    return [
      {
        pass: title.length >= TITLE_MIN && title.length <= TITLE_LIMIT,
        message: `Title should be ${TITLE_MIN}–${TITLE_LIMIT} characters (currently ${title.length}).`,
      },
      {
        pass: description.length >= DESCRIPTION_MIN && description.length <= DESCRIPTION_LIMIT,
        message: `Meta description should be ${DESCRIPTION_MIN}–${DESCRIPTION_LIMIT} characters (currently ${description.length}).`,
      },
      {
        pass: this.hasCoverValue,
        message: "Add a cover image.",
      },
      {
        pass: body.includes("<h2"),
        message: "Add at least one H2 subheading.",
      },
      {
        pass: wordCount >= MIN_BODY_WORDS,
        message: `Body should be at least ${MIN_BODY_WORDS} words (currently ${wordCount}).`,
      },
      {
        pass: /href="\//.test(body) || body.includes("goatleads.com"),
        message: "Add at least one internal link.",
      },
    ]
  }

  _updateScore() {
    const checks = this._checks()
    const passed = checks.filter((check) => check.pass).length
    const score = Math.round((100 * passed) / checks.length)
    const firstFailure = checks.find((check) => !check.pass)

    if (this.hasScoreValueTarget) this.scoreValueTarget.textContent = `${score}`
    if (this.hasScoreHintTarget) {
      this.scoreHintTarget.textContent = firstFailure ? firstFailure.message : "Great — all checks passed."
    }
    if (this.hasScoreRingTarget) {
      this.scoreRingTarget.style.setProperty("--score", score)
      this.scoreRingTarget.classList.toggle("gl-score-ring--good", score >= 80)
      this.scoreRingTarget.classList.toggle("gl-score-ring--warn", score >= 50 && score < 80)
      this.scoreRingTarget.classList.toggle("gl-score-ring--bad", score < 50)
    }
  }
}
